import { useState, useEffect } from 'react';
import { Ban, Trash2, AlertCircle, Plus } from 'lucide-react';
import { api } from '../services/api';
import type { BlockedIp } from '../types';

const EXPIRY_OPTIONS = [
  { label: 'Never', value: '' },
  { label: '1 hour', value: '1' },
  { label: '24 hours', value: '24' },
  { label: '7 days', value: '168' },
  { label: '30 days', value: '720' },
];

export default function BlockedIpsTab() {
  const [loading, setLoading] = useState(true);
  const [blockedIps, setBlockedIps] = useState<BlockedIp[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [form, setForm] = useState({ cidr: '', reason: '', expiresInHours: '' });

  useEffect(() => {
    loadBlockedIps();
  }, []);

  const loadBlockedIps = async () => {
    try {
      setBlockedIps(await api.getBlockedIps());
    } catch (error) {
      console.error('Failed to load blocked IPs:', error);
      setMessage({ type: 'error', text: 'Failed to load blocked IPs' });
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.cidr.trim()) return;

    setSaving(true);
    setMessage(null);
    try {
      await api.blockIp({
        cidr: form.cidr.trim(),
        reason: form.reason.trim() || undefined,
        expires_in_hours: form.expiresInHours ? Number(form.expiresInHours) : undefined,
      });
      setForm({ cidr: '', reason: '', expiresInHours: '' });
      await loadBlockedIps();
      setMessage({ type: 'success', text: 'Block added' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.error || 'Failed to add block',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUnblock = async (blocked: BlockedIp) => {
    if (!window.confirm(`Remove the block on ${blocked.cidr}?`)) return;

    try {
      await api.unblockIp(blocked.id);
      setBlockedIps((current) => current.filter((item) => item.id !== blocked.id));
    } catch (error) {
      console.error('Failed to remove block:', error);
      setMessage({ type: 'error', text: 'Failed to remove block' });
    }
  };

  const formatExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return 'Never expires';
    const date = new Date(expiresAt);
    return date.getTime() <= Date.now()
      ? 'Expired'
      : `Expires ${date.toLocaleString()}`;
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Ban className="w-5 h-5" />
          Blocked IPs
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          The chat widget will not load for these addresses, and they cannot start or continue
          conversations. Accepts a single address or a CIDR block (IPv4 or IPv6).
        </p>
      </div>

      <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>
          Mobile networks and offices share addresses between many people, so a block can affect
          more visitors than you intend. Prefer a temporary block, and note why you added it.
          Determined abusers can still change address with a VPN.
        </p>
      </div>

      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Add form */}
      <form onSubmit={handleBlock} className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              IP address or CIDR block
            </label>
            <input
              type="text"
              value={form.cidr}
              onChange={(e) => setForm({ ...form, cidr: e.target.value })}
              placeholder="203.0.113.5 or 203.0.113.0/24"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <select
              value={form.expiresInHours}
              onChange={(e) => setForm({ ...form, expiresInHours: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
            >
              {EXPIRY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Repeated spam through the widget"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>

        <button
          type="submit"
          disabled={saving || !form.cidr.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          {saving ? 'Blocking…' : 'Block address'}
        </button>
      </form>

      {/* List */}
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : blockedIps.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-gray-300 rounded-lg">
          <Ban className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No blocked addresses</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
          {blockedIps.map((blocked) => (
            <div key={blocked.id} className="flex items-start gap-4 p-4">
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm text-gray-900">{blocked.cidr}</p>
                {blocked.reason && (
                  <p className="text-sm text-gray-600 mt-0.5 break-words">{blocked.reason}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {formatExpiry(blocked.expires_at)}
                  {' • '}
                  Added {new Date(blocked.created_at).toLocaleDateString()}
                  {blocked.created_by_name ? ` by ${blocked.created_by_name}` : ''}
                </p>
              </div>
              <button
                onClick={() => handleUnblock(blocked)}
                title="Remove block"
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
